import React, { useState, useRef } from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from '../App';

const VoiceRecorder = ({ onRecordingComplete, uploading }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], `recording_${Date.now()}.webm`, { type: 'audio/webm' });
        onRecordingComplete(file);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      toast.success('Enregistrement démarré 🎙️');
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Impossible d\'accéder au microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
      toast.success('Enregistrement terminé');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex flex-col items-center space-y-4">
      {!isRecording ? (
        <Button
          onClick={startRecording}
          disabled={uploading}
          data-testid="btn-start-recording"
          className="bg-pink-500/10 hover:bg-pink-500/20 text-pink-400 border-2 border-pink-500/50 hover:border-pink-400 uppercase tracking-widest font-bold px-6 md:px-8 py-6 md:py-8 text-sm md:text-base transition-all duration-300 w-full"
        >
          <Mic className="w-5 h-5 md:w-6 md:h-6 mr-2 md:mr-3" />
          {uploading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Transcription...
            </>
          ) : (
            'Enregistrer maintenant'
          )}
        </Button>
      ) : (
        <div className="w-full space-y-4">
          <div className="bg-slate-950/50 border-2 border-pink-500 rounded p-6 md:p-8 text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="w-3 h-3 md:w-4 md:h-4 bg-red-500 rounded-full animate-pulse mr-3" />
              <span className="text-pink-400 text-lg md:text-xl font-bold font-mono">{formatTime(recordingTime)}</span>
            </div>
            <p className="text-slate-300 text-sm md:text-base mb-4">Enregistrement en cours...</p>
            <Button
              onClick={stopRecording}
              data-testid="btn-stop-recording"
              className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border-2 border-red-500/50 hover:border-red-400 uppercase tracking-widest font-bold px-6 md:px-8 py-4 md:py-6 text-sm md:text-base"
            >
              <Square className="w-4 h-4 md:w-5 md:h-5 mr-2" fill="currentColor" />
              Arrêter l'enregistrement
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder;
